import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useUnsavedChanges } from './utils/useUnsavedChanges';
import { FaUser, FaStethoscope, FaVial, FaShieldAlt, FaTruck, FaChevronLeft, FaChevronRight, FaCloudUploadAlt, FaTimes, FaShareAlt, FaIdCard, FaAddressCard, FaCheckCircle, FaClock, FaClipboardList, FaDollarSign, FaCreditCard } from 'react-icons/fa';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  FormControl,
  FormLabel,
  Icon,
  Button,
  useToast,
  Container,
  Grid,
  GridItem,
  Image,
  IconButton,
  useColorModeValue,
  Select,
  Checkbox,
  Flex,
  Table,
  Thead,
  Tr,
  Tbody,
  Td,
  Th,
  Link,
  Spinner,
  Collapse,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Progress,
  Alert,
  AlertIcon,
  Badge,
  Divider,
  SimpleGrid,
  InputGroup,
  InputLeftElement,
} from '@chakra-ui/react';
import { Image as ChakraImage } from '@chakra-ui/react';
import { supabase } from './supabaseClient';
import { useParams, useLocation, Link as RouterLink, useSearchParams } from 'react-router-dom';
import { analyzeImage } from './aiService'; // Import the AI service
import { CheckCircleIcon, WarningIcon, SearchIcon, CloseIcon, DeleteIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';
import { getLabs, getTestTypesByLab } from './utils/labUtils';

const initialState = {
  patientName: '',
  patientAddress: '',
  patientEmail: '',
  patientPhone: '',
  patientDOB: '',
  patientGender: '',
  doctorName: '',
  labOrderNumber: '',
  doctorAddress: '',
  doctorPhone: '',
  doctorFax: '',
  doctorEmail: '',
  clientName: '',
  labId: '',
  testId: '',
  selectedTests: [],
  bloodCollectionTime: '',
  scriptImage: [],
  insuranceCardImage: [],
  patientIdImage: [],
  isSelfPay: false,
  insuranceId: '',
  policyNumber: '',
  groupNumber: '',
  subscriberId: '',
  relationshipToSubscriber: 'Self',
  needFedexLabel: false,
  fedexShipFrom: '',
  statTest: false,
  specialInstructions: '',
  specimens: [{ quantity: 1, type: '' }],
  diagnosis: [''],
};

const steps = [
  { label: 'Doctor Info', icon: FaStethoscope },
  { label: 'Patient Info', icon: FaUser },
  { label: 'Insurance', icon: FaShieldAlt },
  { label: 'Available Labs', icon: FaTruck },
  { label: 'FedEx Label', icon: FaTruck },
  { label: 'Review', icon: FaClipboardList },
];

// DropZone component for drag-and-drop file upload with glassmorphism, animation, and remove button
const DropZone = ({ label, file, setFile, preview, setPreview, icon: Icon, handleUpload, fileNames, setFileNames, ...props }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const names = fileArray.map(f => f.name);
    setFile(fileArray);
    setPreview(fileArray.map(f => URL.createObjectURL(f)));
    setFileNames(names);
    if (handleUpload) {
      handleUpload(fileArray);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = (index) => {
    const newFiles = file.filter((_, i) => i !== index);
    const newPreviews = preview.filter((_, i) => i !== index);
    const newNames = fileNames.filter((_, i) => i !== index);
    setFile(newFiles);
    setPreview(newPreviews);
    setFileNames(newNames);
  };

  return (
    <FormControl {...props}>
      <FormLabel color="gray.500">{label}</FormLabel>
      <Box
        onClick={() => inputRef.current.click()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        tabIndex={0}
        role="button"
        aria-label={label}
        p={8}
        borderWidth={2}
        borderStyle="dashed"
        borderRadius="xl"
        bg="gray.100"
        borderColor={dragActive ? 'blue.400' : 'gray.300'}
        _hover={{ borderColor: 'blue.400', transform: 'scale(1.02)' }}
        transition="all 0.3s"
        cursor="pointer"
        textAlign="center"
        position="relative"
        overflow="hidden"
      >
        <VStack spacing={4}>
          <Icon as={Icon} boxSize={12} color="blue.500" />
          <Text fontSize="lg" fontWeight="medium" color="gray.700">
            {dragActive ? 'Drop files here' : 'Drag & drop files here or click to browse'}
          </Text>
          <Text fontSize="sm" color="gray.500">
            Supports PDF, JPG, PNG files
          </Text>
        </VStack>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="application/pdf,image/*"
          onChange={handleChange}
          style={{ display: 'none' }}
        />
      </Box>
      
      {/* File Previews */}
      {preview && preview.length > 0 && (
        <VStack spacing={2} mt={4} align="stretch">
          {preview.map((url, index) => (
            <Box key={index} p={3} bg="white" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200">
              <Flex justify="space-between" align="center">
                <HStack>
                  <Icon as={FaCloudUploadAlt} color="green.500" />
                  <Text fontSize="sm" fontWeight="medium">{fileNames[index] || 'Uploaded file'}</Text>
                </HStack>
                <IconButton
                  icon={<FaTimes />}
                  size="sm"
                  variant="ghost"
                  colorScheme="red"
                  onClick={() => removeFile(index)}
                  aria-label="Remove file"
                />
              </Flex>
            </Box>
          ))}
        </VStack>
      )}
    </FormControl>
  );
};

// Helper to normalize possible UUID values coming from URL/params
function normalizeUuid(value) {
  if (!value) return null;
  if (value === 'undefined' || value === 'null') return null;
  return value;
}

// Generate a unique sequential accession / draw_code (starts at 1001)
// Paginates through ALL submissions to find the true max draw code
async function generateDrawCode() {
  try {
    let allCodes = [];
    let from = 0;
    const pageSize = 1000;
    while (true) {
      const { data } = await supabase
        .from('submissions')
        .select('draw_code')
        .not('draw_code', 'is', null)
        .range(from, from + pageSize - 1);
      if (!data || data.length === 0) break;
      allCodes = allCodes.concat(data);
      if (data.length < pageSize) break;
      from += pageSize;
    }
    let max = 1000;
    for (const row of allCodes) {
      const num = parseInt(row.draw_code, 10);
      if (!isNaN(num) && num > max) max = num;
    }
    return String(max + 1);
  } catch {
    return String(Date.now()).slice(-8);
  }
}

async function insertWithDrawCodeRetry(dbData, maxRetries = 5) {
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    if (!dbData.draw_code || attempt > 0) {
      dbData.draw_code = await generateDrawCode();
      if (attempt > 0) {
        dbData.draw_code = String(parseInt(dbData.draw_code, 10) + attempt);
      }
    }
    const { data, error } = await supabase.from('submissions').insert([dbData]).select('id').single();
    if (!error) return { data, error: null };
    if (error.message?.includes('draw_code') && attempt < maxRetries - 1) {
      console.warn(`Draw code collision (attempt ${attempt + 1}), retrying...`);
      continue;
    }
    return { data: null, error };
  }
  return { data: null, error: { message: 'Failed to generate unique draw code after retries' } };
}

// Helper to map camelCase form fields to snake_case DB columns
function mapFormToDb(form, labInfo, labId, insurances, dbColumns = {}) {
  // Check if current user is an admin
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const createdByUser = adminUser?.name || 'LAB';

  // Resolve insurance company name from the insurances list
  const selectedIns = (insurances || []).find(i => i.id === form.insuranceId);
  const insuranceCompanyName = selectedIns?.name || '';

  return {
    patient_name: form.patientName || '',
    patient_street: form.patientAddress || '',
    patient_email: form.patientEmail || '',
    patient_phone: form.patientPhone || '',
    patient_dob: form.patientDOB || '',
    patient_gender: form.patientGender || '',
    doctor_name: form.doctorName || '',
    doctor_address: form.doctorAddress || '',
    doctor_phone: form.doctorPhone || '',
    doctor_fax: form.doctorFax || '',
    doctor_email: form.doctorEmail || '',
    client_name: form.clientName || null,
    lab_order_number: form.labOrderNumber || null,
    lab_id: normalizeUuid(form.labId),
    // Store selected tests in JSONB array per new schema
    tests_on_the_script: form.selectedTests?.length > 0
      ? form.selectedTests.map((t) => ({ id: t.id }))
      : form.testId ? [{ id: form.testId }] : [],
    blood_collection_time: form.bloodCollectionTime || '',
    script_image: Array.isArray(form.scriptImage) ? form.scriptImage : [],
    insurance_card_image: Array.isArray(form.insuranceCardImage) ? form.insuranceCardImage : [],
    patient_id_image: Array.isArray(form.patientIdImage) ? form.patientIdImage : [],
    is_self_pay: form.isSelfPay || false,
    insurance_id: form.isSelfPay ? null : normalizeUuid(form.insuranceId),
    insurance_company: form.isSelfPay ? 'Self Pay' : insuranceCompanyName,
    insurance_policy_number: form.isSelfPay ? '' : (form.policyNumber || ''),
    policy_number: form.isSelfPay ? '' : (form.policyNumber || ''),
    group_number: form.isSelfPay ? '' : (form.groupNumber || ''),
    subscriber_id: form.isSelfPay ? '' : (form.subscriberId || ''),
    relationship_to_subscriber: form.isSelfPay ? 'Self' : (form.relationshipToSubscriber || 'Self'),
    need_fedex_label: form.needFedexLabel || false,
    fedex_ship_from: form.fedexShipFrom || '',
    stat_test: form.statTest || false,
    special_instructions: form.specialInstructions || '',
    ...(dbColumns.specimen_type && form.specimens?.some(s => s.type) ? { specimen_type: JSON.stringify(form.specimens.filter(s => s.type)) } : {}),
    ...(dbColumns.diagnosis && Array.isArray(form.diagnosis) && form.diagnosis.some(d => d.trim()) ? { diagnosis: form.diagnosis.filter(d => d.trim()).join('; ') } : {}),
    phlebotomist_name: labInfo?.company_name || labInfo?.full_name || '',
    phlebotomist_email: labInfo?.email || '',
    phlebotomist_id: normalizeUuid(labId),
    created_by_user: createdByUser,
  };
}

export default function BloodDrawForm({ phlebotomistId, isPatientMode, forcedLabId, hideLabPicker }) {
  const [form, setForm] = useState(initialState);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [scriptPreview, setScriptPreview] = useState([]);
  const [insurancePreview, setInsurancePreview] = useState([]);
  const [patientIdPreview, setPatientIdPreview] = useState([]);
  const [step, setStep] = useState(0);
  const [labInfo, setLabInfo] = useState(null);
  const [pastRequests, setPastRequests] = useState([]);
  const [deliveryTemplates, setDeliveryTemplates] = useState([]);
  const [saveConfirmation, setSaveConfirmation] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(true);
  const toast = useToast();
  const { id, patientEmail: urlPatientEmail } = useParams();
  const location = useLocation();
  const [patientEmailForShare, setPatientEmailForShare] = useState("");
  const [showShareLink, setShowShareLink] = useState(false);
  const [copied, setCopied] = useState(false);
  const [upcomingDraws, setUpcomingDraws] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const submissionIdFromUrlRaw = searchParams.get('submissionId');
  const submissionIdFromUrl = submissionIdFromUrlRaw && !isNaN(Number(submissionIdFromUrlRaw)) && Number(submissionIdFromUrlRaw) > 0 ? Number(submissionIdFromUrlRaw) : null;
  const [submissionId, setSubmissionId] = useState(null);
  const submissionIdRef = useRef(null);
  const isDraftCreated = useRef(false);
  const draftCreatedTimestamp = useRef(null); // Track when draft was created
  // When the form is opened with ?submissionId= in the URL, we need to load the existing row
  // before rendering — otherwise the user sees a blank form flash before the data hydrates.
  const [loadingExistingSubmission, setLoadingExistingSubmission] = useState(!!submissionIdFromUrl);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const navigate = useNavigate();

  // Home route depends on which portal mounted us. Without this, lab-portal
  // users get sent to /phlebotomist/undefined because `id` from useParams is
  // empty in /lab-portal/new-draw.
  const goHome = () => {
    if (id)               navigate(`/phlebotomist/${id}`);
    else if (forcedLabId) navigate('/lab-portal/dashboard');
    else                  navigate('/');
  };
  const [selectedAddress, setSelectedAddress] = useState("");
  const [needFedexLabelChoice, setNeedFedexLabelChoice] = useState(null);
  const [showPatientDoctorLab, setShowPatientDoctorLab] = useState(false);
  const [showInsuranceInfo, setShowInsuranceInfo] = useState(false);
  const [showPatientInfo, setShowPatientInfo] = useState(false);
  const [doctorSearch, setDoctorSearch] = useState('');
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [doctorLoading, setDoctorLoading] = useState(false);
  const [showDoctorDropdown, setShowDoctorDropdown] = useState(false);
  const [debouncedDoctorSearch] = useDebounce(doctorSearch, 500);
  const doctorDropdownRef = useRef(null);
  const [selectedTests, setSelectedTests] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [testTypesLoading, setTestTypesLoading] = useState(false);
  const [testSearch, setTestSearch] = useState('');
  const [filteredTestTypes, setFilteredTestTypes] = useState([]);
  const [showTestDropdown, setShowTestDropdown] = useState(false);
  const dropdownRef = useRef(null);
  const specimenTypes = [
    'Blood - General',
    'BLOOD SMEAR',
    'Blue Top tube',
    'Ear Swab',
    'Endocervical swab',
    'Eye Swab',
    'Fecal Smear',
    'Fecal Swab',
    'Fluid',
    'Gray Top tube - Sodium Flouride',
    'GREEN-TOP TUBE',
    'HAIR',
    'iFOBT Stool',
    'Lavender Top Tube',
    'Lesion swab',
    'Light Blue Tube',
    'MICROGRAPH',
    'MISC',
    'Nail Clippings',
    'NAILS',
    'Nasal (mid-turbinate) swab',
    'Nasal Swab (Default)',
    'Nasopharyngeal swab',
    'Oral Fluid Toxicology',
    'Oral Swab',
    'Oropharyngeal swab',
    'Para-Pak',
    'PCR',
    'Pink Top tube',
    'Plain Red Top Tube',
    'Plasma',
    'Royal Blue Top Tube',
    'Serum',
    'Serum Separator tube',
    'SPUTUM',
    'Stool,Fresh',
    'Swab',
    'Synovial fluid',
    'Urea breath bags',
    'Urethral swab',
    'Urine Cup',
    'Urine in Gray top tube',
    'Urine Molecular',
    'Urine Toxicology',
    'Vaginal swab',
    'Whole Blood',
    'Wound',
    'Yellow Top Tube - ACD A or B',
  ];
  const [specimenSearches, setSpecimenSearches] = useState({});
  const [showSpecimenDropdown, setShowSpecimenDropdown] = useState(null);
  const specimenDropdownRef = useRef(null);

  const [previousDiagnoses, setPreviousDiagnoses] = useState([]);
  const [showDiagnosisDropdown, setShowDiagnosisDropdown] = useState(false);
  const diagnosisDropdownRef = useRef(null);

  // Track which optional columns exist in the submissions table
  const [dbColumns, setDbColumns] = useState({ specimen_type: false, diagnosis: false });
  useEffect(() => {
    const checkColumns = async () => {
      const cols = { specimen_type: false, diagnosis: false };
      for (const col of ['specimen_type', 'diagnosis']) {
        const { error } = await supabase.from('submissions').select(col).limit(1);
        if (!error) cols[col] = true;
      }
      setDbColumns(cols);
    };
    checkColumns();
  }, []);

  // Store original file names separately for display after upload
  const [scriptFileNames, setScriptFileNames] = useState([]);
  const [insuranceFileNames, setInsuranceFileNames] = useState([]);
  const [patientIdFileNames, setPatientIdFileNames] = useState([]);
  
  // Warn user about unsaved changes when navigating away
  const hasFormData = form.patientName || form.doctorName || form.selectedTests?.length > 0;
  useUnsavedChanges(hasFormData && !formSubmitted);

  // Manual lab addition for exceptions
  const [manualLabId, setManualLabId] = useState('');
  const [manualLabLoading, setManualLabLoading] = useState(false);
  
  // Insurance state
  const [insurances, setInsurances] = useState([]);
  const [insurancesLoading, setInsurancesLoading] = useState(false);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  // Labs state
  const [labs, setLabs] = useState([]);
  const [labsLoading, setLabsLoading] = useState(false);
  const [labSearch, setLabSearch] = useState('');
  const [debouncedLabSearch] = useDebounce(labSearch, 400);
  const [showLabDropdown, setShowLabDropdown] = useState(false);
  const labDropdownRef = useRef(null);
  const [selectedLab, setSelectedLab] = useState(null);
  
  // Patient state for searchable dropdown
  const [patients, setPatients] = useState([]);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientSearch, setPatientSearch] = useState('');
  const [showPatientDropdown, setShowPatientDropdown] = useState(false);
  const patientDropdownRef = useRef(null);
  const [isNewPatient, setIsNewPatient] = useState(false);
  const [newPatientForm, setNewPatientForm] = useState({
    name: '',
    email: '',
    dob: '',
    address: ''
  });

  // Fetch lab info
  const fetchLabInfo = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('phlebotomist_profiles')
        .select('*')
        .eq('id', id)
        .single();
      
      if (!error && data) {
        setLabInfo(data);
      }
    } catch (error) {
      setLabInfo(null);
    }
  }, [id]);

  // Fetch all rows with pagination to bypass Supabase 1000-row cap
  const fetchAllRows = useCallback(async (table, selectQuery, orderCol = 'name') => {
    const PAGE = 1000;
    let allData = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table).select(selectQuery).order(orderCol).range(from, from + PAGE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    return allData;
  }, []);

  // Fetch test types from both tests and panels tables
  const fetchTestTypes = useCallback(async () => {
    setTestTypesLoading(true);
    try {
      const selectCols = 'id, name, code, description, cash_price, alternative_name';
      const [testsData, panelsData] = await Promise.all([
        fetchAllRows('tests', selectCols),
        fetchAllRows('panels', selectCols),
      ]);
      const allTests = [
        ...testsData.map(t => ({ ...t, type: 'test' })),
        ...panelsData.map(p => ({ ...p, type: 'panel' })),
      ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setTestTypes(allTests);
      setFilteredTestTypes(allTests.slice(0, 20));
    } catch (error) {
      console.error('Error fetching test types:', error);
    } finally {
      setTestTypesLoading(false);
    }
  }, [fetchAllRows]);

  // Fetch previous diagnoses from submissions (only if column exists)
  const fetchPreviousDiagnoses = useCallback(async () => {
    try {
      const { error: colCheck } = await supabase.from('submissions').select('diagnosis').limit(1);
      if (colCheck) return; // Column doesn't exist yet
      const { data } = await supabase.from('submissions').select('diagnosis').not('diagnosis', 'is', null).not('diagnosis', 'eq', '').limit(500);
      const unique = [...new Set((data || []).map(d => d.diagnosis).filter(Boolean))].sort();
      setPreviousDiagnoses(unique);
    } catch (error) {
      console.error('Error fetching previous diagnoses:', error);
    }
  }, []);

  // Fetch available insurances
  const fetchInsurances = useCallback(async () => {
    setInsurancesLoading(true);
    try {
      const { data, error } = await supabase
        .from('insurances')
        .select('*')
        .eq('is_active', true)
        .order('name');
      
      if (error) {
        console.error('Error fetching insurances:', error);
      } else if (data) {
        setInsurances(data);
      }
    } catch (error) {
      console.error('Error fetching insurances:', error);
    } finally {
      setInsurancesLoading(false);
    }
  }, []);

  // Fetch available labs for Lab Brand select
  const fetchLabs = useCallback(async (searchTerm) => {
    setLabsLoading(true);
    try {
      const q = (searchTerm || '').trim();
      let query = supabase
        .from('labs')
        .select('id, name, contact_email, city, state')
        .order('name')
        .limit(30);

      if (q) {
        // PostgREST's or() treats commas as list separators and parens as grouping,
        // so a lab name like "LINCOLN DIAGNOSTICS, LLC" breaks the filter. Strip
        // those characters from the search term before splicing it in.
        const safe = q.replace(/[,()*]/g, ' ').trim();
        if (safe) {
          query = query.or(`name.ilike.%${safe}%,city.ilike.%${safe}%,state.ilike.%${safe}%`);
        }
      }

      const { data, error } = await query;
      if (error) {
        console.error('Error fetching labs:', error);
      } else if (data) {
        setLabs(data);
      }
    } catch (error) {
      console.error('Error fetching labs:', error);
    } finally {
      setLabsLoading(false);
    }
  }, []);

  // Fetch patients for the searchable dropdown.
  //   - Phleb route (id set): patients this phleb has drawn for previously.
  //   - Lab route (forcedLabId set, id null): patients who have submissions for this lab.
  useEffect(() => {
    const fetchPatients = async () => {
      if (!id && !forcedLabId) return; // need at least one scope

      try {
        setPatientsLoading(true);
        // Order by submitted_at DESC so the most recent row per patient name comes first.
        // Then dedupe "keep first" → the freshest contact info wins. (Old code sorted by
        // patient_name and used Map.set which kept an arbitrary row, surfacing stale
        // addresses/emails for patients who'd moved between visits.)
        // NOTE: submissions has submitted_at, NOT created_at — using created_at made the
        // whole query fail silently (Postgres error → console.error → empty dropdown).
        let query = supabase
          .from('submissions')
          .select('patient_name, patient_email, patient_phone, patient_dob, patient_street, submitted_at')
          .order('submitted_at', { ascending: false })
          .limit(200);
        if (id) {
          query = query.eq('phlebotomist_id', id);
        } else if (forcedLabId) {
          query = query.eq('lab_id', forcedLabId);
        }
        const { data, error } = await query;
        if (!error && data) {
          const seen = new Set();
          const uniquePatients = [];
          data.forEach((p) => {
            const key = (p.patient_name || '').trim();
            if (key && !seen.has(key)) {
              seen.add(key);
              uniquePatients.push(p);
            }
          });
          // Display alphabetically in the dropdown
          uniquePatients.sort((a, b) => (a.patient_name || '').localeCompare(b.patient_name || ''));
          setPatients(uniquePatients);
        }
      } catch (e) {
        console.error('Error loading patients', e);
      } finally {
        setPatientsLoading(false);
      }
    };
    fetchPatients();
  }, [id, forcedLabId]);

  // Handle adding new patient (simplified for phlebotomist - just name)
  const handleAddNewPatient = async () => {
    if (!newPatientForm.name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter at least the patient name',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // For phlebotomist, we just use the name and let admin complete the rest
    setForm(f => ({
      ...f,
      patientName: newPatientForm.name,
      patientEmail: newPatientForm.email || '',
      patientDOB: newPatientForm.dob || '',
      patientAddress: newPatientForm.address || ''
    }));
    
    setPatientSearch(newPatientForm.name);
    setIsNewPatient(false);
    setShowPatientDropdown(false);

    toast({
      title: 'Patient added',
      description: 'Patient name saved. Admin can complete remaining details.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  // Fetch doctors list for searchable dropdown
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setDoctorLoading(true);
        const { data, error } = await supabase
          .from('doctors')
          .select('id, full_name, email, phone, clinic_name, address')
          .order('full_name');
        if (!error && data) {
          setDoctorOptions(data);
        }
      } catch (e) {
        console.error('Error loading doctors', e);
      } finally {
        setDoctorLoading(false);
      }
    };
    fetchDoctors();
  }, []);

  // Fetch labs when search term or insurance changes (debounced)
  useEffect(() => {
    fetchLabs(debouncedLabSearch, form.insuranceId);
  }, [debouncedLabSearch, fetchLabs]);

  // Acronym matching (e.g. "BMP" matches "Basic Metabolic Panel")
  const matchesAcronym = useCallback((name, search) => {
    if (!name || !search) return false;
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length < 2) return false;
    const acronym = words.map(w => w[0]).join('').toLowerCase();
    return acronym === search || acronym.startsWith(search);
  }, []);

  // Filter test types based on search (name, alternative_name, acronym)
  useEffect(() => {
    if (testSearch.trim() === '') {
      setFilteredTestTypes(testTypes.slice(0, 20));
    } else {
      const q = testSearch.toLowerCase();
      const filtered = testTypes.filter(test =>
        (test.name || '').toLowerCase().includes(q) ||
        (test.alternative_name || '').toLowerCase().includes(q) ||
        matchesAcronym(test.name, q)
      );
      setFilteredTestTypes(filtered);
    }
  }, [testSearch, testTypes, matchesAcronym]);

  // Click outside handler to close dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowTestDropdown(false);
      }
      if (doctorDropdownRef.current && !doctorDropdownRef.current.contains(event.target)) {
        setShowDoctorDropdown(false);
      }
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(event.target)) {
        setShowPatientDropdown(false);
      }
      if (labDropdownRef.current && !labDropdownRef.current.contains(event.target)) {
        setShowLabDropdown(false);
      }
      if (specimenDropdownRef.current && !specimenDropdownRef.current.contains(event.target)) {
        setShowSpecimenDropdown(null);
      }
      if (diagnosisDropdownRef.current && !diagnosisDropdownRef.current.contains(event.target)) {
        setShowDiagnosisDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Set default labId from phlebotomist's reference lab when labInfo is loaded
  useEffect(() => {
    if (labInfo && labInfo.lab_id && !form.labId) {
      setForm(f => ({ ...f, labId: labInfo.lab_id }));
    }
  }, [labInfo, form.labId]);

  // When the lab portal opens this form, the lab is already known — pin it.
  useEffect(() => {
    if (forcedLabId && form.labId !== forcedLabId) {
      setForm(f => ({ ...f, labId: forcedLabId }));
    }
  }, [forcedLabId, form.labId]);

  // When labId is set (from existing submission or default), fetch that lab's info for the search bar
  useEffect(() => {
    const fetchSelectedLab = async () => {
      if (!form.labId || selectedLab?.id === form.labId) return;
      try {
        const { data, error } = await supabase
          .from('labs')
          .select('id, name, contact_email, city, state')
          .eq('id', form.labId)
          .single();
        if (!error && data) {
          setSelectedLab(data);
          setLabSearch(data.name || '');
        }
      } catch (e) {
        console.error('Error fetching selected lab:', e);
      }
    };
    fetchSelectedLab();
  }, [form.labId]);

  // Initialize submissionId from URL to prevent creating duplicates
  useEffect(() => {
    if (submissionIdFromUrl && submissionIdFromUrl > 0) {
      setSubmissionId(submissionIdFromUrl);
      isDraftCreated.current = true; // Prevent creating a new draft
    }
  }, [submissionIdFromUrl]);

  // Fetch and load existing submission data when submissionId is available
  useEffect(() => {
    const fetchExistingSubmission = async () => {
      if (!submissionId) { setLoadingExistingSubmission(false); return; }

      // For numeric IDs, check if it's valid
      if (typeof submissionId === 'number' && submissionId <= 0) { setLoadingExistingSubmission(false); return; }
      if (typeof submissionId === 'string' && !isNaN(parseInt(submissionId)) && parseInt(submissionId) <= 0) { setLoadingExistingSubmission(false); return; }

      try {
        // Handle both numeric IDs and UUIDs
        const submissionIdValue = isNaN(parseInt(submissionId)) ? submissionId : parseInt(submissionId);

        const { data, error } = await supabase
          .from('submissions')
          .select('*')
          .eq('id', submissionIdValue)
          .single();

        if (error) {
          console.error('Error fetching submission:', error);
          setLoadingExistingSubmission(false);
          return;
        }
        
        if (data) {
          
          // CRITICAL: Only redirect to summary if user EXPLICITLY opened form with submissionId in URL.
          // This means admin sent them a link to review/confirm an existing submission.
          // Do NOT redirect when:
          // - User is creating a NEW submission (no submissionId in URL)
          // - We just created a draft in this session (even if it has lab_id from phlebotomist's default)
          const openedViaUrl = submissionIdFromUrl != null && String(submissionId) === String(submissionIdFromUrl);
          
          // Check if this submission was just created in this session
          const submissionCreatedAt = data.created_at ? new Date(data.created_at).getTime() : null;
          const justCreatedInSession = draftCreatedTimestamp.current && 
            submissionCreatedAt && 
            Math.abs(submissionCreatedAt - draftCreatedTimestamp.current) < 10000; // 10 second window
          
          // ONLY redirect if:
          // 1. User opened form WITH submissionId in URL (openedViaUrl = true) - this is the key check
          // 2. Submission has lab_id or lab_brand (lab already chosen)
          // 3. We did NOT just create this draft in this session
          // If no submissionId in URL, user is creating NEW submission - NEVER redirect
          if (openedViaUrl && !justCreatedInSession && (data.lab_id || data.lab_brand)) {
            navigate(`/phlebotomist/${id}/summary/${submissionId}`, { replace: true });
            return;
          }
          
          // Log for debugging
          if (!openedViaUrl && (data.lab_id || data.lab_brand)) {
          }
          if (justCreatedInSession && (data.lab_id || data.lab_brand)) {
          }
          
          // Map the submission data back to form fields (functional updater to preserve selectedTests)
          setForm(prev => ({
            ...prev,
            patientName: data.patient_name || '',
            patientAddress: data.patient_street || data.patient_address || '',
            patientEmail: data.patient_email || '',
            patientPhone: data.patient_phone || '',
            patientDOB: data.patient_dob || '',
            doctorName: data.doctor_name || '',
            labOrderNumber: data.lab_order_number || '',
            doctorAddress: data.doctor_address || '',
            doctorPhone: data.doctor_phone || '',
            doctorFax: data.doctor_fax || '',
            doctorEmail: data.doctor_email || '',
            clientName: data.client_name || '',
            labId: data.lab_id || '',
            testId: data.test_type_id || '',
            bloodCollectionTime: (() => {
              const v = data.blood_collection_time || '';
              if (!v) return '';
              const d = new Date(v);
              if (isNaN(d.getTime()) || d.getFullYear() > 2099 || d.getFullYear() < 1900) return '';
              return v.slice(0, 16);
            })(),
            scriptImage: Array.isArray(data.script_image) ? data.script_image : [],
            insuranceCardImage: Array.isArray(data.insurance_card_image) ? data.insurance_card_image : [],
            patientIdImage: Array.isArray(data.patient_id_image) ? data.patient_id_image : [],
            isSelfPay: data.is_self_pay || false,
            insuranceId: data.insurance_id || '',
            policyNumber: data.policy_number || '',
            groupNumber: data.group_number || '',
            subscriberId: data.subscriber_id || '',
            relationshipToSubscriber: data.relationship_to_subscriber || 'Self',
            needFedexLabel: data.need_fedex_label || false,
            fedexShipFrom: data.fedex_ship_from || '',
            statTest: data.stat_test || false,
            specialInstructions: data.special_instructions || ''
          }));
          
          // Set preview URLs for uploaded images
          if (data.script_image) {
            setScriptPreview(Array.isArray(data.script_image) ? data.script_image : [data.script_image]);
          }
          if (data.insurance_card_image) {
            setInsurancePreview(Array.isArray(data.insurance_card_image) ? data.insurance_card_image : [data.insurance_card_image]);
          }
          if (data.patient_id_image) {
            setPatientIdPreview(Array.isArray(data.patient_id_image) ? data.patient_id_image : [data.patient_id_image]);
          }

          // Restore selectedTests from tests_on_the_script
          if (data.tests_on_the_script && Array.isArray(data.tests_on_the_script) && data.tests_on_the_script.length > 0) {
            const testIds = data.tests_on_the_script.map(t => t.id).filter(Boolean);
            if (testIds.length > 0) {
              // Fetch test details (name, code) from both tests and panels tables
              const [testsRes, panelsRes] = await Promise.all([
                supabase.from('tests').select('id, name, code').in('id', testIds),
                supabase.from('panels').select('id, name, code').in('id', testIds),
              ]);
              const resolved = [
                ...(testsRes.data || []),
                ...(panelsRes.data || []),
              ];
              // Build full test objects, falling back to id-only if not found
              const restoredTests = testIds.map(tid => {
                const found = resolved.find(r => r.id === tid);
                return found || { id: tid, name: tid };
              });
              setSelectedTests(restoredTests);
              setForm(prev => ({ ...prev, selectedTests: restoredTests }));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching submission:', error);
      } finally {
        setLoadingExistingSubmission(false);
      }
    };

    fetchExistingSubmission();
  }, [submissionId, id, navigate]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Year validation for bloodCollectionTime
    if (name === 'bloodCollectionTime' && value) {
      const yearMatch = value.match(/^(\d{4})/);
      if (yearMatch) {
        const year = parseInt(yearMatch[1], 10);
        if (year > 2099 || year < 1900 || yearMatch[1].length > 4) return;
      }
    }

    const newForm = type === 'checkbox'
      ? { ...form, [name]: checked }
      : { ...form, [name]: value };
    setForm(newForm);
  };

  const handleImageUpload = async (files, type) => {
    if (!files || files.length === 0) return [];

    const urls = [];
    for (const file of files) {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${type}/${fileName}`;

      const { error } = await supabase.storage
        .from('uploads')
        .upload(filePath, file, { contentType: file.type || 'application/octet-stream', upsert: true });

      if (error) {
        console.error('Upload error:', error);
        continue;
      }

      const { data: { publicUrl } } = supabase.storage
        .from('uploads')
        .getPublicUrl(filePath);

      urls.push(publicUrl);
    }

    return urls;
  };

  const [showPaymentScreen, setShowPaymentScreen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [submittedSelfPayData, setSubmittedSelfPayData] = useState(null);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setLoading(true);

    try {
      // If we're editing an existing submission (opened via the View / URL ?submissionId=...),
      // UPDATE the row instead of inserting a new one. Otherwise we'd create a duplicate
      // patient record every time the phleb saved an edit.
      const existingId = submissionId || submissionIdFromUrl || null;
      let data;
      if (existingId) {
        const updateData = mapFormToDb(form, labInfo, id, insurances, dbColumns);
        // Preserve the original draw_code, submitted_at, and status — never overwrite on edit.
        delete updateData.draw_code;
        delete updateData.submitted_at;
        delete updateData.status;
        const { data: updated, error } = await supabase
          .from('submissions')
          .update(updateData)
          .eq('id', existingId)
          .select('id')
          .single();
        if (error) throw error;
        data = updated;
      } else {
        const dbData = { ...mapFormToDb(form, labInfo, id, insurances, dbColumns), status: 'pending', submitted_at: new Date().toISOString() };
        dbData.draw_code = await generateDrawCode();
        const { data: inserted, error } = await insertWithDrawCodeRetry(dbData);
        if (error) throw error;
        data = inserted;
      }
      if (data) setSubmissionId(data.id);

      toast({
        title: 'Success!',
        description: existingId ? 'Blood draw request updated successfully.' : 'Blood draw request submitted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // If self-pay, show payment screen instead of redirecting
      if (form.isSelfPay && data?.id) {
        const drawFee = labInfo?.lab_draw_fee ? parseFloat(labInfo.lab_draw_fee) : 0;
        const testCosts = selectedTests.reduce((sum, t) => sum + (t.cash_price ? parseFloat(t.cash_price) : 0), 0);
        setSubmittedSelfPayData({
          submissionId: data.id,
          drawFee,
          tests: selectedTests.map(t => ({ name: t.name, price: t.cash_price ? parseFloat(t.cash_price) : 0 })),
          total: drawFee + testCosts,
        });
        setFormSubmitted(true);
        setShowPaymentScreen(true);
        return;
      }

      // Reset form
      setFormSubmitted(true);
      setForm(initialState);
      setScriptPreview([]);
      setInsurancePreview([]);
      setPatientIdPreview([]);

      // Navigate back to the dashboard after a brief delay. The form is
      // mounted in three contexts; pick the right home route for each:
      //   • phleb portal — has :id in the URL → /phlebotomist/:id
      //   • lab portal   — id is undefined, forcedLabId set → /lab-portal/dashboard
      //   • patient mode — neither; just go home
      setTimeout(() => {
        if (id)               navigate(`/phlebotomist/${id}`);
        else if (forcedLabId) navigate('/lab-portal/dashboard');
        else                  navigate('/');
      }, 1500);

    } catch (error) {
      console.error('Submit error:', error);
      toast({
        title: 'Error',
        description: error?.message || 'Failed to submit request. Please try again.',
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSelfPayCheckout = async () => {
    if (!submittedSelfPayData?.submissionId) return;
    setCheckoutLoading(true);
    try {
      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/create-selfpay-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ submissionId: submittedSelfPayData.submissionId }),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error?.message || 'Failed to create checkout session');
      if (result.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast({
        title: 'Payment Error',
        description: error.message || 'Failed to start checkout. You can pay later.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCheckoutLoading(false);
    }
  };

  // Step content
  const renderStep = () => {
    if (isPatientMode && step > 1) {
      return null; // Only show the first two steps in patient mode
    }
    switch (step) {
      case 0:
        return (
          <VStack spacing={8} align="stretch">
            {/* Step 1: Doctor Name + Drag and Drop */}
            <Box mb={6}>
              <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Step 1: Doctor Information</Text>
              
              {/* Doctor Name Field with searchable dropdown */}
              <FormControl position="relative" mb={6}>
                <FormLabel fontSize="lg" fontWeight="semibold">Doctor Name</FormLabel>
                <Input
                  value={doctorSearch || form.doctorName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setDoctorSearch(val);
                    setShowDoctorDropdown(true);
                  }}
                  onFocus={() => setShowDoctorDropdown(true)}
                  placeholder="Search by name, clinic, or email..."
                  name="doctorName"
                  id="doctorName"
                  size="lg"
                  fontSize="md"
                />

                {/* Doctor dropdown */}
                {showDoctorDropdown && (
                  <Box
                    ref={doctorDropdownRef}
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    boxShadow="lg"
                    maxH="300px"
                    overflowY="auto"
                    zIndex={30}
                    mt={1}
                  >
                    {doctorLoading && (
                      <Text px={3} py={2} fontSize="sm" color="gray.500">Loading doctors...</Text>
                    )}

                    {!doctorLoading && doctorOptions
                      .filter(d => {
                        const q = (doctorSearch || '').toLowerCase();
                        return (
                          d.full_name?.toLowerCase().includes(q) ||
                          d.clinic_name?.toLowerCase().includes(q) ||
                          d.email?.toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 20)
                      .map((d) => (
                        <Box
                          key={d.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          onClick={() => {
                            setForm(f => ({
                              ...f,
                              doctorName: d.full_name || '',
                              doctorEmail: d.email || '',
                              doctorPhone: d.phone || '',
                              doctorAddress: d.address || '',
                              doctorFax: f.doctorFax,
                              clientName: d.clinic_name || '',
                            }));
                            setDoctorSearch(d.full_name || '');
                            setShowDoctorDropdown(false);
                          }}
                        >
                          <Text fontWeight="medium">{d.full_name}</Text>
                          {d.clinic_name && (
                            <Text fontSize="sm" color="gray.600">{d.clinic_name}</Text>
                          )}
                          {d.email && (
                            <Text fontSize="xs" color="gray.500">{d.email}</Text>
                          )}
                        </Box>
                      ))}

                    {/* Sticky Create New option */}
                    <Box position="sticky" bottom={0} bg="white" borderTop="1px solid" borderColor="gray.200">
                      <Box
                        px={3}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: 'green.50' }}
                        onClick={() => {
                          // Switch input to the current search text as the doctor name
                          setForm(f => ({ ...f, doctorName: doctorSearch }));
                          setShowDoctorDropdown(false);
                          toast({
                            title: 'Create new doctor',
                            description: 'Enter details and continue. The admin can formalize the record later.',
                            status: 'info',
                            duration: 2000,
                            isClosable: true,
                          });
                        }}
                      >
                        <Text fontWeight="semibold" color="green.600">+ Create new doctor...</Text>
                      </Box>
                    </Box>
                  </Box>
                )}
              </FormControl>

              {/* Client Name - editable; pre-filled when doctor selected */}
              <FormControl mt={4}>
                <FormLabel fontSize="lg" fontWeight="semibold">Client Name</FormLabel>
                <Input
                  value={form.clientName || ''}
                  onChange={(e) => setForm((f) => ({ ...f, clientName: e.target.value }))}
                  placeholder="Facility or client name (e.g. senior living, practice)"
                  size="lg"
                />
              </FormControl>

              {/* Order ID — staff only. Shows to the phlebotomist as "Order #…",
                  same as lab-API orders. Hidden in patient booking mode. */}
              {!isPatientMode && (
                <FormControl mt={4}>
                  <FormLabel fontSize="lg" fontWeight="semibold">Order ID</FormLabel>
                  <Input
                    value={form.labOrderNumber || ''}
                    onChange={(e) => setForm((f) => ({ ...f, labOrderNumber: e.target.value }))}
                    placeholder="Lab order ID (optional) — shown to the phlebotomist"
                    size="lg"
                  />
                </FormControl>
              )}

              {/* Drag and Drop Box */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={4}>Doctor's Script</Text>
                <DropZone
                  label="Upload Doctor's Script"
                  file={form.scriptImage}
                  setFile={(files) => setForm((f) => ({ ...f, scriptImage: files }))}
                  preview={scriptPreview}
                  setPreview={setScriptPreview}
                  fileNames={scriptFileNames}
                  setFileNames={setScriptFileNames}
                  mb={4}
                  icon={FaStethoscope}
                  handleUpload={async (files) => {
                    const urls = await handleImageUpload(files, 'script');
                    setForm(f => ({ ...f, scriptImage: urls }));
                  }}
                />
              </Box>

              {/* Test Types Selection */}
              <Box>
                <Text fontSize="lg" fontWeight="semibold" mb={4}>Tests Ordered</Text>

                {/* Selected Tests Badges */}
                {selectedTests.length > 0 && (
                  <Flex flexWrap="wrap" gap={2} mb={3}>
                    {selectedTests.map((test) => (
                      <Badge
                        key={test.id}
                        colorScheme="teal"
                        variant="solid"
                        px={3}
                        py={1}
                        borderRadius="full"
                        fontSize="sm"
                        cursor="pointer"
                        onClick={() => {
                          setSelectedTests(prev => prev.filter(t => t.id !== test.id));
                          setForm(f => ({ ...f, selectedTests: f.selectedTests.filter(t => t.id !== test.id) }));
                        }}
                      >
                        {test.name} {test.code ? `(${test.code})` : ''} &times;
                      </Badge>
                    ))}
                  </Flex>
                )}

                <FormControl position="relative" ref={dropdownRef}>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      value={testSearch}
                      onChange={(e) => {
                        setTestSearch(e.target.value);
                        setShowTestDropdown(true);
                      }}
                      onFocus={() => setShowTestDropdown(true)}
                      placeholder="Search tests by name, code, or description..."
                      size="md"
                    />
                  </InputGroup>

                  {showTestDropdown && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                      boxShadow="lg"
                      maxH="250px"
                      overflowY="auto"
                      zIndex={30}
                      mt={1}
                    >
                      {testTypesLoading && (
                        <HStack px={3} py={3} justify="center">
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.500">Loading tests...</Text>
                        </HStack>
                      )}
                      {!testTypesLoading && filteredTestTypes.length === 0 && (
                        <Text px={3} py={3} fontSize="sm" color="gray.500" textAlign="center">
                          No tests found
                        </Text>
                      )}
                      {!testTypesLoading && filteredTestTypes.map((test) => (
                        <Box
                          key={`${test.type}-${test.id}`}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          bg={selectedTests.some(t => t.id === test.id) ? 'blue.100' : 'transparent'}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          onClick={() => {
                            if (!selectedTests.some(t => t.id === test.id)) {
                              const newTests = [...selectedTests, test];
                              setSelectedTests(newTests);
                              setForm(f => ({ ...f, selectedTests: newTests }));
                            }
                            setShowTestDropdown(false);
                            setTestSearch('');
                          }}
                        >
                          <HStack justify="space-between">
                            <Box>
                              <Text fontWeight="medium" fontSize="sm">{test.name}</Text>
                              {test.code && <Text fontSize="xs" color="gray.500">Code: {test.code}</Text>}
                              {test.alternative_name && <Text fontSize="xs" color="gray.400">{test.alternative_name}</Text>}
                            </Box>
                            {test.type === 'panel' && (
                              <Badge colorScheme="purple" fontSize="xs">Panel</Badge>
                            )}
                          </HStack>
                        </Box>
                      ))}
                    </Box>
                  )}
                </FormControl>
              </Box>

              {/* Specimen Type - Multi-row */}
              <Box ref={specimenDropdownRef}>
                <Text fontSize="lg" fontWeight="semibold" mb={3}>Specimen</Text>
                <Box border="1px solid" borderColor="gray.200" borderRadius="md">
                  {/* Header */}
                  <HStack bg="gray.100" px={3} py={2} spacing={4}>
                    <Text fontSize="sm" fontWeight="bold" w="80px">Quantity</Text>
                    <Text fontSize="sm" fontWeight="bold" flex={1}>Specimen</Text>
                    <Box w="32px" />
                  </HStack>
                  {/* Rows */}
                  {(form.specimens || []).map((row, idx) => {
                    const searchVal = specimenSearches[idx] || '';
                    const filtered = searchVal.trim()
                      ? specimenTypes.filter(s => s.toLowerCase().includes(searchVal.toLowerCase()))
                      : specimenTypes;
                    return (
                      <HStack key={idx} px={3} py={2} spacing={4} borderTop="1px solid" borderColor="gray.100" align="flex-start">
                        <Select size="sm" w="80px" value={row.quantity}
                          onChange={(e) => {
                            const updated = [...form.specimens];
                            updated[idx] = { ...updated[idx], quantity: parseInt(e.target.value) };
                            setForm(f => ({ ...f, specimens: updated }));
                          }}
                        >
                          {[1,2,3,4,5,6,7,8,9,10].map(n => <option key={n} value={n}>{n}</option>)}
                        </Select>
                        <Box flex={1} position="relative">
                          {row.type ? (
                            <HStack bg="orange.50" border="1px solid" borderColor="orange.200" borderRadius="md" px={3} py={1} justify="space-between">
                              <Text fontSize="sm">{row.type}</Text>
                              <Text fontSize="xs" cursor="pointer" color="gray.400" _hover={{ color: 'red.500' }}
                                onClick={() => {
                                  const updated = [...form.specimens];
                                  updated[idx] = { ...updated[idx], type: '' };
                                  setForm(f => ({ ...f, specimens: updated }));
                                }}
                              >&times;</Text>
                            </HStack>
                          ) : (
                            <>
                              <Input size="sm" placeholder="Search specimen..."
                                value={searchVal}
                                onChange={(e) => {
                                  setSpecimenSearches(prev => ({ ...prev, [idx]: e.target.value }));
                                  setShowSpecimenDropdown(idx);
                                }}
                                onFocus={() => setShowSpecimenDropdown(idx)}
                              />
                              {showSpecimenDropdown === idx && (
                                <Box position="absolute" top="100%" left={0} right={0} bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" boxShadow="lg" maxH="200px" overflowY="auto" zIndex={30} mt={1}>
                                  {filtered.length === 0 && (
                                    <Text px={3} py={2} fontSize="sm" color="gray.500" textAlign="center">No matches</Text>
                                  )}
                                  {filtered.map((s) => (
                                    <Box key={s} px={3} py={1.5} cursor="pointer" fontSize="sm" _hover={{ bg: 'blue.50' }}
                                      borderBottom="1px solid" borderColor="gray.50"
                                      onClick={() => {
                                        const updated = [...form.specimens];
                                        updated[idx] = { ...updated[idx], type: s };
                                        setForm(f => ({ ...f, specimens: updated }));
                                        setShowSpecimenDropdown(null);
                                        setSpecimenSearches(prev => ({ ...prev, [idx]: '' }));
                                      }}
                                    >
                                      {s}
                                    </Box>
                                  ))}
                                </Box>
                              )}
                            </>
                          )}
                        </Box>
                        <IconButton icon={<CloseIcon />} size="xs" variant="ghost" colorScheme="red" aria-label="Remove row"
                          isDisabled={form.specimens.length <= 1}
                          onClick={() => {
                            const updated = form.specimens.filter((_, i) => i !== idx);
                            setForm(f => ({ ...f, specimens: updated }));
                          }}
                        />
                      </HStack>
                    );
                  })}
                </Box>
                <Button size="sm" variant="ghost" colorScheme="blue" mt={2}
                  onClick={() => setForm(f => ({ ...f, specimens: [...(f.specimens || []), { quantity: 1, type: '' }] }))}
                >
                  + Add Row
                </Button>
              </Box>

              {/* Diagnosis */}
              <Box>
                <Flex justify="space-between" align="center" mb={2}>
                  <Text fontSize="lg" fontWeight="semibold">Diagnosis</Text>
                  {previousDiagnoses.length > 0 && (
                    <Box ref={diagnosisDropdownRef} position="relative">
                      <Button size="sm" colorScheme="blue" onClick={() => setShowDiagnosisDropdown(!showDiagnosisDropdown)}>
                        Previous Diagnosis
                      </Button>
                      {showDiagnosisDropdown && (
                        <Box position="absolute" top="100%" right={0} bg="white" border="1px solid" borderColor="gray.300" borderRadius="md" boxShadow="lg" maxH="200px" overflowY="auto" zIndex={30} mt={1} minW="250px">
                          {previousDiagnoses.map((d) => (
                            <Box key={d} px={3} py={2} cursor="pointer" _hover={{ bg: 'blue.50' }} borderBottom="1px solid" borderColor="gray.100"
                              onClick={() => {
                                const diagArr = Array.isArray(form.diagnosis) ? form.diagnosis : [''];
                                const emptyIdx = diagArr.findIndex(v => !v.trim());
                                const updated = [...diagArr];
                                if (emptyIdx >= 0) {
                                  updated[emptyIdx] = d;
                                } else {
                                  updated.push(d);
                                }
                                setForm(f => ({ ...f, diagnosis: updated }));
                                setShowDiagnosisDropdown(false);
                              }}
                            >
                              <Text fontSize="sm">{d}</Text>
                            </Box>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Flex>
                {(Array.isArray(form.diagnosis) ? form.diagnosis : [form.diagnosis || '']).map((diag, idx) => (
                  <HStack key={idx} mb={2}>
                    <Input
                      value={diag}
                      onChange={(e) => {
                        const updated = [...(Array.isArray(form.diagnosis) ? form.diagnosis : [form.diagnosis || ''])];
                        updated[idx] = e.target.value;
                        setForm(f => ({ ...f, diagnosis: updated }));
                      }}
                      placeholder="Enter diagnosis or ICD code..."
                      size="md"
                    />
                    <IconButton
                      icon={<DeleteIcon />}
                      size="sm"
                      variant="ghost"
                      colorScheme="red"
                      aria-label="Remove diagnosis"
                      isDisabled={(Array.isArray(form.diagnosis) ? form.diagnosis : [form.diagnosis || '']).length <= 1}
                      onClick={() => {
                        const updated = (Array.isArray(form.diagnosis) ? form.diagnosis : [form.diagnosis || '']).filter((_, i) => i !== idx);
                        setForm(f => ({ ...f, diagnosis: updated }));
                      }}
                    />
                  </HStack>
                ))}
                <Button
                  size="sm"
                  variant="link"
                  colorScheme="blue"
                  onClick={() => setForm(f => ({ ...f, diagnosis: [...(Array.isArray(f.diagnosis) ? f.diagnosis : [f.diagnosis || '']), ''] }))}
                >
                  + Add Row
                </Button>
              </Box>
            </Box>
          </VStack>
        );
      case 1:
        return (
          <VStack spacing={8} align="stretch">
            <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Step 2: Patient Information</Text>
            
            {/* Patient Name - Searchable Dropdown */}
            <Box mb={6}>
              <FormControl position="relative">
                <FormLabel fontSize="lg" fontWeight="semibold">Patient Name</FormLabel>
                <HStack spacing={2} mb={2}>
                  <Text fontSize="sm" color="gray.500">Select existing or create new</Text>
                </HStack>
                <Input
                  value={patientSearch || form.patientName}
                  onChange={(e) => {
                    const val = e.target.value;
                    setPatientSearch(val);
                    setShowPatientDropdown(true);
                  }}
                  onFocus={() => setShowPatientDropdown(true)}
                  placeholder="Search by name..."
                  name="patientName"
                  id="patientName"
                  size="lg"
                  fontSize="md"
                />

                {/* Patient dropdown */}
                {showPatientDropdown && (
                  <Box
                    ref={patientDropdownRef}
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.300"
                    borderRadius="md"
                    boxShadow="lg"
                    maxH="300px"
                    overflowY="auto"
                    zIndex={30}
                    mt={1}
                  >
                    {patientsLoading && (
                      <Text px={3} py={2} fontSize="sm" color="gray.500">Loading patients...</Text>
                    )}

                    {!patientsLoading && patients
                      .filter(p => {
                        const q = (patientSearch || '').toLowerCase();
                        return p.patient_name?.toLowerCase().includes(q) ||
                               p.patient_email?.toLowerCase().includes(q);
                      })
                      .slice(0, 20)
                      .map((p) => (
                        <Box
                          key={p.patient_name}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          onClick={() => {
                            setForm(f => ({
                              ...f,
                              patientName: p.patient_name || '',
                              patientEmail: p.patient_email || '',
                              patientPhone: p.patient_phone || '',
                              patientDOB: p.patient_dob || '',
                              patientAddress: p.patient_street || ''
                            }));
                            setPatientSearch(p.patient_name || '');
                            setShowPatientDropdown(false);
                          }}
                        >
                          <Text fontWeight="medium">{p.patient_name}</Text>
                          {p.patient_email && (
                            <Text fontSize="sm" color="gray.600">{p.patient_email}</Text>
                          )}
                          {p.patient_street && (
                            <Text fontSize="xs" color="gray.500">{p.patient_street}</Text>
                          )}
                        </Box>
                      ))}

                    {/* Sticky Create New option */}
                    <Box position="sticky" bottom={0} bg="white" borderTop="1px solid" borderColor="gray.200">
                      <Box
                        px={3}
                        py={2}
                        cursor="pointer"
                        _hover={{ bg: 'green.50' }}
                        onClick={() => {
                          setIsNewPatient(true);
                          setShowPatientDropdown(false);
                        }}
                      >
                        <Text fontWeight="semibold" color="green.600">+ Create new patient...</Text>
                      </Box>
                    </Box>
                  </Box>
                )}
              </FormControl>

              {/* New Patient Form */}
              {isNewPatient && (
                <Box mt={4} p={4} bg="gray.50" borderRadius="md" border="1px solid" borderColor="gray.300">
                  <VStack spacing={4} align="stretch">
                    <Text fontSize="md" fontWeight="bold">New Patient</Text>
                    <Text fontSize="sm" color="gray.600">
                      Only name is required. Admin will complete remaining details.
                    </Text>
                    <Input
                      placeholder="Patient Name *"
                      value={newPatientForm.name}
                      onChange={(e) => setNewPatientForm(f => ({ ...f, name: e.target.value }))}
                      size="lg"
                    />
                    <HStack spacing={2}>
                      <Button colorScheme="green" size="md" onClick={handleAddNewPatient} flex={1}>
                        Add Patient
                      </Button>
                      <Button colorScheme="gray" size="md" onClick={() => setIsNewPatient(false)} flex={1}>
                        Cancel
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              )}

              {/* Display selected patient info */}
              {form.patientName && !isNewPatient && (
                <Box mt={4} p={3} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" color="blue.700">{form.patientName}</Text>
                    {form.patientEmail && (
                      <Text fontSize="sm" color="blue.600">Email: {form.patientEmail}</Text>
                    )}
                    {form.patientPhone && (
                      <Text fontSize="sm" color="blue.600">Phone: {form.patientPhone}</Text>
                    )}
                    {form.patientDOB && (
                      <Text fontSize="sm" color="blue.600">DOB: {form.patientDOB}</Text>
                    )}
                    {form.patientGender && (
                      <Text fontSize="sm" color="blue.600">Gender: {form.patientGender}</Text>
                    )}
                    {form.patientAddress && (
                      <Text fontSize="sm" color="blue.600">Address: {form.patientAddress}</Text>
                    )}
                  </VStack>
                </Box>
              )}
            </Box>
            
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
              <GridItem>
                <FormControl>
                  <FormLabel>Patient DOB</FormLabel>
                  <Input
                    name="patientDOB"
                    type="date"
                    value={form.patientDOB}
                    onChange={handleChange}
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl isRequired>
                  <FormLabel>Patient Gender</FormLabel>
                  <Select
                    name="patientGender"
                    value={form.patientGender}
                    onChange={handleChange}
                    placeholder="Select gender"
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Patient Phone</FormLabel>
                  <Input
                    name="patientPhone"
                    type="tel"
                    value={form.patientPhone}
                    onChange={handleChange}
                    placeholder="(555) 123-4567"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Patient Email</FormLabel>
                  <Input
                    name="patientEmail"
                    type="email"
                    value={form.patientEmail}
                    onChange={handleChange}
                    placeholder="patient@example.com"
                  />
                </FormControl>
              </GridItem>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl>
                  <FormLabel>Patient Address</FormLabel>
                  <Input
                    name="patientAddress"
                    value={form.patientAddress}
                    onChange={handleChange}
                    placeholder="Enter patient address"
                  />
                </FormControl>
              </GridItem>
            </Grid>

            {/* Patient ID Upload */}
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4}>Patient ID</Text>
              <DropZone
                label="Upload Patient ID"
                file={form.patientIdImage}
                setFile={(files) => setForm((f) => ({ ...f, patientIdImage: files }))}
                preview={patientIdPreview}
                setPreview={setPatientIdPreview}
                fileNames={patientIdFileNames}
                setFileNames={setPatientIdFileNames}
                icon={FaAddressCard}
                handleUpload={async (files) => {
                  const urls = await handleImageUpload(files, 'patientId');
                  setForm(f => ({ ...f, patientIdImage: urls }));
                }}
              />
            </Box>
          </VStack>
        );
      case 2:
        return (
          <VStack spacing={8} align="stretch">
            <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Step 3: Insurance Information</Text>

            {/* Payment Type Toggle */}
            <SimpleGrid columns={2} spacing={4}>
              <Box
                p={6}
                borderRadius="lg"
                border="2px solid"
                borderColor={!form.isSelfPay ? 'blue.500' : 'gray.200'}
                bg={!form.isSelfPay ? 'blue.50' : 'white'}
                cursor="pointer"
                onClick={() => setForm(f => ({ ...f, isSelfPay: false }))}
                transition="all 0.2s"
                _hover={{ borderColor: 'blue.400', shadow: 'md' }}
              >
                <VStack spacing={3}>
                  <Icon as={FaShieldAlt} boxSize={8} color={!form.isSelfPay ? 'blue.500' : 'gray.400'} />
                  <Text fontWeight="bold" fontSize="lg">Insurance</Text>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    Patient has health insurance coverage
                  </Text>
                </VStack>
              </Box>
              <Box
                p={6}
                borderRadius="lg"
                border="2px solid"
                borderColor={form.isSelfPay ? 'green.500' : 'gray.200'}
                bg={form.isSelfPay ? 'green.50' : 'white'}
                cursor="pointer"
                onClick={() => {
                  setForm(f => ({ ...f, isSelfPay: true, insuranceId: '', policyNumber: '', groupNumber: '' }));
                  setSelectedInsurance(null);
                }}
                transition="all 0.2s"
                _hover={{ borderColor: 'green.400', shadow: 'md' }}
              >
                <VStack spacing={3}>
                  <Icon as={FaDollarSign} boxSize={8} color={form.isSelfPay ? 'green.500' : 'gray.400'} />
                  <Text fontWeight="bold" fontSize="lg">Self Pay</Text>
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    No insurance — patient pays out of pocket
                  </Text>
                </VStack>
              </Box>
            </SimpleGrid>

            {/* Self Pay Info with Pricing */}
            {form.isSelfPay && (
              <Box p={5} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                <VStack spacing={4} align="stretch">
                  <HStack>
                    <Icon as={FaCreditCard} color="green.600" />
                    <Text fontWeight="bold" color="green.700">Self Pay Selected</Text>
                  </HStack>

                  {/* Draw Fee */}
                  {labInfo?.lab_draw_fee && (
                    <Box p={4} bg="white" borderRadius="md" border="1px solid" borderColor="green.100">
                      <HStack justify="space-between">
                        <Text fontSize="sm" color="gray.600">Blood Draw Fee</Text>
                        <Text fontWeight="bold" fontSize="lg" color="green.700">
                          ${parseFloat(labInfo.lab_draw_fee).toFixed(2)}
                        </Text>
                      </HStack>
                    </Box>
                  )}

                  {/* Selected Tests Pricing */}
                  {selectedTests.length > 0 && (
                    <Box p={4} bg="white" borderRadius="md" border="1px solid" borderColor="green.100">
                      <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={2}>Test Costs</Text>
                      <VStack spacing={2} align="stretch">
                        {selectedTests.map((test) => (
                          <HStack key={test.id} justify="space-between">
                            <Text fontSize="sm" color="gray.600">{test.name}</Text>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                              {test.cash_price ? `$${parseFloat(test.cash_price).toFixed(2)}` : 'Price TBD'}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    </Box>
                  )}

                  {/* Total */}
                  <Divider borderColor="green.200" />
                  <HStack justify="space-between" px={2}>
                    <Text fontWeight="bold" color="green.700">Estimated Total</Text>
                    <Text fontWeight="bold" fontSize="xl" color="green.700">
                      ${(
                        (labInfo?.lab_draw_fee ? parseFloat(labInfo.lab_draw_fee) : 0) +
                        selectedTests.reduce((sum, t) => sum + (t.cash_price ? parseFloat(t.cash_price) : 0), 0)
                      ).toFixed(2)}
                    </Text>
                  </HStack>

                  <Text fontSize="xs" color="gray.500">
                    Payment will be collected at the time of service or via invoice. Tests marked "Price TBD" will be confirmed before service.
                  </Text>
                </VStack>
              </Box>
            )}

            {/* Insurance Fields - only show when NOT self pay */}
            {!form.isSelfPay && (
              <>
                <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Insurance Provider</FormLabel>
                      <Select
                        name="insuranceId"
                        value={form.insuranceId}
                        onChange={(e) => {
                          const selectedId = e.target.value;
                          setForm(f => ({ ...f, insuranceId: selectedId }));
                          const insurance = insurances.find(i => i.id === selectedId);
                          setSelectedInsurance(insurance);
                        }}
                        isDisabled={insurancesLoading}
                      >
                        <option value="">Select insurance provider</option>
                        {insurances.map((insurance) => (
                          <option key={insurance.id} value={insurance.id}>
                            {insurance.name} ({insurance.type})
                          </option>
                        ))}
                      </Select>
                      {insurancesLoading && (
                        <Text fontSize="sm" color="gray.500" mt={2}>Loading insurance providers...</Text>
                      )}
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Policy Number</FormLabel>
                      <Input
                        name="policyNumber"
                        value={form.policyNumber}
                        onChange={handleChange}
                        placeholder="Enter policy number"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Group Number</FormLabel>
                      <Input
                        name="groupNumber"
                        value={form.groupNumber}
                        onChange={handleChange}
                        placeholder="Enter group number (optional)"
                      />
                    </FormControl>
                  </GridItem>
                  <GridItem>
                    <FormControl>
                      <FormLabel>Relationship to Subscriber</FormLabel>
                      <Select
                        name="relationshipToSubscriber"
                        value={form.relationshipToSubscriber}
                        onChange={handleChange}
                      >
                        <option value="Self">Self</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Child">Child</option>
                        <option value="Other">Other</option>
                      </Select>
                    </FormControl>
                  </GridItem>
                </Grid>

                {/* Insurance Information Display */}
                {selectedInsurance && (
                  <Box p={4} bg="blue.50" borderRadius="md" border="1px solid" borderColor="blue.200">
                    <VStack align="start" spacing={2}>
                      <Text fontWeight="bold" color="blue.700">
                        Selected Insurance: {selectedInsurance.name}
                      </Text>
                      <Text fontSize="sm" color="blue.600">
                        Type: {selectedInsurance.type}
                      </Text>
                      {selectedInsurance.description && (
                        <Text fontSize="sm" color="blue.600">
                          {selectedInsurance.description}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                )}

                {/* Insurance Card Upload */}
                <Box>
                  <Text fontSize="lg" fontWeight="semibold" mb={4}>Insurance Card</Text>
                  <DropZone
                    label="Upload Insurance Card"
                    file={form.insuranceCardImage}
                    setFile={(files) => setForm((f) => ({ ...f, insuranceCardImage: files }))}
                    preview={insurancePreview}
                    setPreview={setInsurancePreview}
                    fileNames={insuranceFileNames}
                    setFileNames={setInsuranceFileNames}
                    icon={FaIdCard}
                    handleUpload={async (files) => {
                      const urls = await handleImageUpload(files, 'insurance');
                      setForm(f => ({ ...f, insuranceCardImage: urls }));
                    }}
                  />
                </Box>
              </>
            )}
          </VStack>
        );
      case 3:
        return (
          <VStack spacing={8} align="stretch">
            <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Step 5: Available Labs</Text>

            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={6}>
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <FormControl position="relative">
                  <FormLabel>Assign Laboratory</FormLabel>
                  {hideLabPicker ? (
                    <Box bg="gray.50" borderWidth="1px" borderRadius="md" px={4} py={3}>
                      <Text fontWeight="600">{selectedLab?.name || 'Loading lab…'}</Text>
                      {(selectedLab?.city || selectedLab?.state) && (
                        <Text fontSize="sm" color="gray.500">{[selectedLab.city, selectedLab.state].filter(Boolean).join(', ')}</Text>
                      )}
                      <Text fontSize="xs" color="gray.400" mt={1}>Draw will be assigned to your lab.</Text>
                    </Box>
                  ) : (
                  <Input
                    value={labSearch}
                    onChange={(e) => {
                      setLabSearch(e.target.value);
                      setShowLabDropdown(true);
                      if (!e.target.value.trim()) {
                        setSelectedLab(null);
                        setForm(f => ({ ...f, labId: '' }));
                      }
                    }}
                    onFocus={() => setShowLabDropdown(true)}
                    placeholder="Search by lab name, brand, city, or state..."
                    size="lg"
                    fontSize="md"
                  />
                  )}

                  {/* Lab search dropdown */}
                  {showLabDropdown && (
                    <Box
                      ref={labDropdownRef}
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      bg="white"
                      border="1px solid"
                      borderColor="gray.300"
                      borderRadius="md"
                      boxShadow="lg"
                      maxH="300px"
                      overflowY="auto"
                      zIndex={30}
                      mt={1}
                    >
                      {labsLoading && (
                        <HStack px={3} py={3} justify="center">
                          <Spinner size="sm" />
                          <Text fontSize="sm" color="gray.500">Searching labs...</Text>
                        </HStack>
                      )}

                      {!labsLoading && labs.length === 0 && (
                        <Text px={3} py={3} fontSize="sm" color="gray.500" textAlign="center">
                          {labSearch.trim() ? 'No labs found. Try a different search.' : 'Type to search for a laboratory...'}
                        </Text>
                      )}

                      {!labsLoading && labs.map((lab) => (
                        <Box
                          key={lab.id}
                          px={3}
                          py={2}
                          cursor="pointer"
                          _hover={{ bg: 'blue.50' }}
                          bg={form.labId === lab.id ? 'blue.100' : 'transparent'}
                          borderBottom="1px solid"
                          borderColor="gray.100"
                          onClick={() => {
                            setSelectedLab(lab);
                            setForm(f => ({ ...f, labId: lab.id }));
                            setLabSearch(lab.name || '');
                            setShowLabDropdown(false);
                          }}
                        >
                          <Text fontWeight="medium">{lab.name}</Text>
                          <HStack spacing={2} flexWrap="wrap">
                            {(lab.city || lab.state) && (
                              <Text fontSize="xs" color="gray.500">
                                {[lab.city, lab.state].filter(Boolean).join(', ')}
                              </Text>
                            )}
                          </HStack>
                          {lab.contact_email && (
                            <Text fontSize="xs" color="gray.400">{lab.contact_email}</Text>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Selected lab badge */}
                  {selectedLab && (
                    <HStack mt={2} p={2} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
                      <Icon as={FaCheckCircle} color="green.500" />
                      <Text fontSize="sm" fontWeight="medium" color="green.700">
                        Selected: {selectedLab.name}
                        {(selectedLab.city || selectedLab.state) ? ` — ${[selectedLab.city, selectedLab.state].filter(Boolean).join(', ')}` : ''}
                      </Text>
                    </HStack>
                  )}
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Blood Collection Time</FormLabel>
                  <Input
                    name="bloodCollectionTime"
                    type="datetime-local"
                    value={form.bloodCollectionTime}
                    onChange={handleChange}
                    min="1900-01-01T00:00"
                    max="2099-12-31T23:59"
                  />
                </FormControl>
              </GridItem>
              <GridItem>
                <FormControl>
                  <FormLabel>Special Instructions</FormLabel>
                  <Input
                    name="specialInstructions"
                    value={form.specialInstructions}
                    onChange={handleChange}
                    placeholder="Any special instructions..."
                  />
                </FormControl>
              </GridItem>
            </Grid>
          </VStack>
        );
      case 4:
        return (
          <VStack spacing={8} align="stretch">
            <Text fontSize="2xl" fontWeight="bold" mb={6} textAlign="center">Step 6: FedEx Shipping</Text>
            <Box>
              <Text fontSize="lg" fontWeight="semibold" mb={4}>Delivery Options</Text>
              <FormControl>
                <FormLabel>Do you need a FedEx label?</FormLabel>
                <HStack spacing={4}>
                  <Button
                    colorScheme={form.needFedexLabel ? 'blue' : 'gray'}
                    variant={form.needFedexLabel ? 'solid' : 'outline'}
                    onClick={() => setForm(f => ({ ...f, needFedexLabel: true }))}
                  >
                    Yes, I need a label
                  </Button>
                  <Button
                    colorScheme={!form.needFedexLabel ? 'gray' : 'blue'}
                    variant={!form.needFedexLabel ? 'solid' : 'outline'}
                    onClick={() => setForm(f => ({ ...f, needFedexLabel: false }))}
                  >
                    No, skip label
                  </Button>
                </HStack>
              </FormControl>
            </Box>
          </VStack>
        );
      case 5: {
        const reviewInsurance = insurances.find(i => i.id === form.insuranceId);
        const reviewLab = selectedLab || labs.find(l => l.id === form.labId);
        return (
          <VStack spacing={6} align="stretch">
            <Text fontSize="2xl" fontWeight="bold" mb={2} textAlign="center">Review & Confirm</Text>
            <Text fontSize="sm" color="gray.500" textAlign="center">Please review the details below before submitting.</Text>

            {/* Doctor Info */}
            <Box bg="green.50" p={4} rounded="lg" borderWidth="1px" borderColor="green.200">
              <HStack mb={3}>
                <Icon as={FaStethoscope} color="green.600" />
                <Text fontWeight="bold" color="green.700">Doctor Info</Text>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                <Box><Text fontSize="xs" color="gray.500">Name</Text><Text fontSize="sm" fontWeight="medium">{form.doctorName || '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Phone</Text><Text fontSize="sm">{form.doctorPhone || '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Email</Text><Text fontSize="sm">{form.doctorEmail || '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Fax</Text><Text fontSize="sm">{form.doctorFax || '—'}</Text></Box>
                {form.doctorAddress && <Box gridColumn="1 / -1"><Text fontSize="xs" color="gray.500">Address</Text><Text fontSize="sm">{form.doctorAddress}</Text></Box>}
              </SimpleGrid>
            </Box>

            {/* Patient Info */}
            <Box bg="blue.50" p={4} rounded="lg" borderWidth="1px" borderColor="blue.200">
              <HStack mb={3}>
                <Icon as={FaUser} color="blue.600" />
                <Text fontWeight="bold" color="blue.700">Patient Info</Text>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                <Box><Text fontSize="xs" color="gray.500">Name</Text><Text fontSize="sm" fontWeight="medium">{form.patientName || '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">DOB</Text><Text fontSize="sm">{form.patientDOB || '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Gender</Text><Text fontSize="sm">{form.patientGender || '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Email</Text><Text fontSize="sm">{form.patientEmail || '—'}</Text></Box>
                {form.patientAddress && <Box gridColumn="1 / -1"><Text fontSize="xs" color="gray.500">Address</Text><Text fontSize="sm">{form.patientAddress}</Text></Box>}
              </SimpleGrid>
            </Box>

            {/* Insurance / Self Pay */}
            {form.isSelfPay ? (
              <Box bg="green.50" p={4} rounded="lg" borderWidth="1px" borderColor="green.200">
                <HStack mb={3}>
                  <Icon as={FaDollarSign} color="green.600" />
                  <Text fontWeight="bold" color="green.700">Self Pay</Text>
                </HStack>
                <VStack spacing={3} align="stretch">
                  {labInfo?.lab_draw_fee && (
                    <HStack justify="space-between">
                      <Text fontSize="sm" color="gray.600">Blood Draw Fee</Text>
                      <Text fontSize="sm" fontWeight="bold" color="green.700">${parseFloat(labInfo.lab_draw_fee).toFixed(2)}</Text>
                    </HStack>
                  )}
                  {selectedTests.length > 0 && selectedTests.map((test) => (
                    <HStack key={test.id} justify="space-between">
                      <Text fontSize="sm" color="gray.600">{test.name}</Text>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                        {test.cash_price ? `$${parseFloat(test.cash_price).toFixed(2)}` : 'Price TBD'}
                      </Text>
                    </HStack>
                  ))}
                  <Divider borderColor="green.200" />
                  <HStack justify="space-between">
                    <Text fontWeight="bold" color="green.700">Estimated Total</Text>
                    <Text fontWeight="bold" fontSize="lg" color="green.700">
                      ${(
                        (labInfo?.lab_draw_fee ? parseFloat(labInfo.lab_draw_fee) : 0) +
                        selectedTests.reduce((sum, t) => sum + (t.cash_price ? parseFloat(t.cash_price) : 0), 0)
                      ).toFixed(2)}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            ) : (
              <Box bg="orange.50" p={4} rounded="lg" borderWidth="1px" borderColor="orange.200">
                <HStack mb={3}>
                  <Icon as={FaShieldAlt} color="orange.600" />
                  <Text fontWeight="bold" color="orange.700">Insurance</Text>
                </HStack>
                {reviewInsurance ? (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                    <Box><Text fontSize="xs" color="gray.500">Provider</Text><Text fontSize="sm" fontWeight="medium">{reviewInsurance.name}</Text></Box>
                    <Box><Text fontSize="xs" color="gray.500">Type</Text><Text fontSize="sm">{reviewInsurance.type}</Text></Box>
                    <Box><Text fontSize="xs" color="gray.500">Policy #</Text><Text fontSize="sm">{form.policyNumber || '—'}</Text></Box>
                    <Box><Text fontSize="xs" color="gray.500">Group #</Text><Text fontSize="sm">{form.groupNumber || '—'}</Text></Box>
                  </SimpleGrid>
                ) : (
                  <Text fontSize="sm" color="gray.500">No insurance selected</Text>
                )}
                {form.insuranceCardImage?.length > 0 && (
                  <Badge mt={2} colorScheme="green" variant="subtle">Insurance card uploaded</Badge>
                )}
              </Box>
            )}

            {/* Labs & Collection */}
            <Box bg="purple.50" p={4} rounded="lg" borderWidth="1px" borderColor="purple.200">
              <HStack mb={3}>
                <Icon as={FaVial} color="purple.600" />
                <Text fontWeight="bold" color="purple.700">Collection Details</Text>
              </HStack>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                <Box><Text fontSize="xs" color="gray.500">Assigned Lab</Text><Text fontSize="sm" fontWeight="medium">{reviewLab ? `${reviewLab.name}${(reviewLab.city || reviewLab.state) ? ` — ${[reviewLab.city, reviewLab.state].filter(Boolean).join(', ')}` : ''}` : '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">Collection Time</Text><Text fontSize="sm">{form.bloodCollectionTime ? new Date(form.bloodCollectionTime).toLocaleString() : '—'}</Text></Box>
                <Box><Text fontSize="xs" color="gray.500">FedEx Label</Text><Text fontSize="sm">{form.needFedexLabel ? 'Yes' : 'No'}</Text></Box>
                {form.specimens?.some(s => s.type) && <Box><Text fontSize="xs" color="gray.500">Specimen</Text>{form.specimens.filter(s => s.type).map((s, i) => <Text key={i} fontSize="sm">{s.quantity}x {s.type}</Text>)}</Box>}
                {(Array.isArray(form.diagnosis) ? form.diagnosis.filter(d => d.trim()) : (form.diagnosis ? [form.diagnosis] : [])).length > 0 && <Box><Text fontSize="xs" color="gray.500">Diagnosis</Text>{(Array.isArray(form.diagnosis) ? form.diagnosis.filter(d => d.trim()) : [form.diagnosis]).map((d, i) => <Text key={i} fontSize="sm">{d}</Text>)}</Box>}
                {form.specialInstructions && <Box gridColumn="1 / -1"><Text fontSize="xs" color="gray.500">Special Instructions</Text><Text fontSize="sm">{form.specialInstructions}</Text></Box>}
              </SimpleGrid>
              {form.scriptImage?.length > 0 && (
                <Badge mt={2} colorScheme="green" variant="subtle">Script uploaded</Badge>
              )}
            </Box>

            {/* Tests Ordered */}
            {selectedTests.length > 0 && (
              <Box bg="teal.50" p={4} rounded="lg" borderWidth="1px" borderColor="teal.200">
                <HStack mb={3}>
                  <Icon as={FaVial} color="teal.600" />
                  <Text fontWeight="bold" color="teal.700">Tests Ordered</Text>
                </HStack>
                <Flex flexWrap="wrap" gap={2}>
                  {selectedTests.map((test) => (
                    <Badge key={test.id} colorScheme="teal" variant="solid" px={3} py={1} borderRadius="full" fontSize="sm">
                      {test.name} {test.code ? `(${test.code})` : ''}
                    </Badge>
                  ))}
                </Flex>
              </Box>
            )}

            <Divider />
            <Alert status="info" rounded="md">
              <AlertIcon />
              <Text fontSize="sm">Click <strong>Submit</strong> to finalize this blood draw request.</Text>
            </Alert>
          </VStack>
        );
      }
      default:
        return null;
    }
  };

  // Progress bar
  const progress = ((step + 1) / steps.length) * 100;

  // Step validation: required fields before allowing Next / Complete
  const isStepValid = (s) => {
    switch (s) {
      case 0:
        return Boolean(form.doctorName?.trim());
      case 1:
        return Boolean(form.patientName?.trim() && form.patientGender);
      case 2:
        return true; // insurance optional
      case 3:
        return true; // Lab/Test UI removed; step always valid
      case 4:
        return true; // FedEx choice always valid
      case 5:
        return true; // Review step always valid
      default:
        return true;
    }
  };
  const canProceed = isStepValid(step);

  // Step navigation
  const handleNext = async (e) => {
    if (!canProceed) return;
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      await handleSubmit(e);
    }
  };

  const handlePrevious = () => {
    if (step > 0) {
      setStep(step - 1);
    }
  };

  // Step navigation component
  const renderStepNavigation = () => (
    <Flex justify="space-between" mt={8}>
      <Button
        leftIcon={<FaChevronLeft />}
        onClick={handlePrevious}
        isDisabled={step === 0}
        variant="outline"
        colorScheme="blue"
        type="button"
      >
        Previous
      </Button>
      <Button
        rightIcon={<FaChevronRight />}
        onClick={handleNext}
        colorScheme="blue"
        type="button"
        isLoading={loading}
        loadingText={step === steps.length - 1 ? "Submitting..." : "Next"}
        isDisabled={!canProceed}
        title={!canProceed ? 'Complete required fields to continue' : undefined}
      >
        {step === steps.length - 1 ? 'Submit' : step === steps.length - 2 ? 'Review' : 'Next'}
      </Button>
    </Flex>
  );

  useEffect(() => {
    // Test types, diagnoses, and insurances are global lookups — fetch them whenever
    // the form is mounted, including the lab-portal new-draw flow where `id` is null
    // because the URL has no phlebotomist param. fetchLabInfo (phleb profile) still
    // requires `id` because the lab path doesn't have a phleb to look up.
    if (id || forcedLabId) {
      fetchTestTypes();
      fetchPreviousDiagnoses();
      fetchInsurances();
    }
    if (id) {
      fetchLabInfo();
    }
  }, [id, forcedLabId, fetchLabInfo, fetchTestTypes, fetchPreviousDiagnoses, fetchInsurances]);

  // If this form was opened with a submissionId and admin already sent to doctor,
  // redirect to condensed summary/selection screen instead of full stepper
  useEffect(() => {
    const maybeRedirectToSummary = async () => {
      try {
        if (!id || !submissionIdFromUrl) return;
        const { data, error } = await supabase
          .from('submissions')
          .select('id, status, manual_lab_options')
          .eq('id', submissionIdFromUrl)
          .single();
        if (!error && data) {
          const hasOptions = Array.isArray(data.manual_lab_options) && data.manual_lab_options.length > 0;
          const sent = data.status && data.status.toLowerCase().includes('in_progress');
          if (hasOptions && sent) {
            navigate(`/phlebotomist/${id}/summary/${submissionIdFromUrl}`);
          }
        }
      } catch {}
    };
    maybeRedirectToSummary();
  }, [id, submissionIdFromUrl, navigate]);

  // Show a spinner while we're hydrating an existing submission from the URL —
  // prevents the empty-form flash before the data lands.
  if (loadingExistingSubmission) {
    return (
      <Box minH="100vh" bg="gray.50" display="flex" alignItems="center" justifyContent="center">
        <VStack spacing={3}>
          <Spinner size="xl" thickness="3px" color="blue.500" />
          <Text fontSize="sm" color="gray.500">Loading submission...</Text>
        </VStack>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg="gray.50" color="gray.800">
      {/* ── Hero Header — Gradient ──────────────────────── */}
      <Box bgGradient="linear(135deg, teal.500, blue.600)" position="relative" overflow="hidden">
        <Box position="absolute" top={-20} right={-10} w="280px" h="280px" bg="whiteAlpha.100" borderRadius="full" />
        <Box position="absolute" bottom={-32} left="30%" w="320px" h="320px" bg="whiteAlpha.50" borderRadius="full" />

        <Container maxW="container.xl" py={5} position="relative">
          <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
            <HStack spacing={4}>
              <Box bg="white" p={2} borderRadius="xl" cursor="pointer" onClick={goHome} boxShadow="md">
                <Image src="/logo.png" alt="AtHomeBloodDraw.com" h="36px" objectFit="contain" />
              </Box>
              {labInfo && (
                <Box color="white">
                  <Text fontSize="lg" fontWeight="800" letterSpacing="-0.3px">{labInfo.company_name || labInfo.full_name || 'Phlebotomist'}</Text>
                  <HStack spacing={3} fontSize="xs" opacity={0.9} flexWrap="wrap">
                    {labInfo.company_address && <Text>{labInfo.company_address}</Text>}
                    {labInfo.email && <Text>•  {labInfo.email}</Text>}
                    {labInfo.phone && <Text>•  {labInfo.phone}</Text>}
                  </HStack>
                </Box>
              )}
            </HStack>
            <Button
              size="sm"
              bg="whiteAlpha.300"
              color="white"
              _hover={{ bg: 'whiteAlpha.400' }}
              backdropFilter="blur(10px)"
              onClick={goHome}
              borderRadius="full"
              px={5}
              fontWeight="600"
            >
              ← Back to Dashboard
            </Button>
          </Flex>
        </Container>
      </Box>

      <Container maxW="container.md" py={8}>
        {showPaymentScreen && submittedSelfPayData ? (
          <VStack spacing={6} align="stretch">
            <Box textAlign="center" mb={4}>
              <Icon as={FaCheckCircle} boxSize={16} color="green.400" mb={4} />
              <Text fontSize="2xl" fontWeight="bold" color="green.700">Request Submitted!</Text>
              <Text color="gray.600" mt={2}>Your blood draw request has been submitted successfully.</Text>
            </Box>

            <Box bg="white" p={6} rounded="xl" shadow="md" borderWidth="1px" borderColor="green.200">
              <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.700">Payment Summary</Text>
              <VStack spacing={3} align="stretch">
                {submittedSelfPayData.drawFee > 0 && (
                  <HStack justify="space-between">
                    <Text color="gray.600">Blood Draw Fee</Text>
                    <Text fontWeight="semibold">${submittedSelfPayData.drawFee.toFixed(2)}</Text>
                  </HStack>
                )}
                {submittedSelfPayData.tests.map((test, i) => (
                  <HStack key={i} justify="space-between">
                    <Text color="gray.600" fontSize="sm">{test.name}</Text>
                    <Text fontWeight="semibold" fontSize="sm">
                      {test.price > 0 ? `$${test.price.toFixed(2)}` : 'Price TBD'}
                    </Text>
                  </HStack>
                ))}
                <Divider />
                <HStack justify="space-between">
                  <Text fontWeight="bold" fontSize="lg">Total</Text>
                  <Text fontWeight="bold" fontSize="xl" color="green.600">
                    ${submittedSelfPayData.total.toFixed(2)}
                  </Text>
                </HStack>
              </VStack>
            </Box>

            <VStack spacing={3}>
              <Button
                colorScheme="green"
                size="lg"
                w="full"
                leftIcon={<FaCreditCard />}
                onClick={handleSelfPayCheckout}
                isLoading={checkoutLoading}
                loadingText="Redirecting to checkout..."
                isDisabled={submittedSelfPayData.total <= 0}
              >
                Pay Now — ${submittedSelfPayData.total.toFixed(2)}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                color="gray.500"
                onClick={goHome}
              >
                Pay Later
              </Button>
              <Text fontSize="xs" color="gray.400" textAlign="center">
                You'll be redirected to a secure Stripe checkout page. You can also choose to pay later.
              </Text>
            </VStack>
          </VStack>
        ) : (
        <VStack spacing={8} align="stretch">

          {/* Progress Bar — Modern stepper */}
          <Box bg="white" p={5} borderRadius="2xl" boxShadow="md">
            <Flex justify="space-between" mb={3}>
              {steps.map((stepItem, index) => (
                <VStack key={index} flex="1" spacing={2}>
                  <Flex
                    w={11}
                    h={11}
                    borderRadius="full"
                    bgGradient={index <= step ? 'linear(135deg, teal.400, blue.500)' : 'linear(135deg, gray.100, gray.200)'}
                    align="center"
                    justify="center"
                    transition="all 0.3s"
                    boxShadow={index === step ? 'lg' : 'sm'}
                    transform={index === step ? 'scale(1.1)' : 'scale(1)'}
                    border="3px solid"
                    borderColor={index === step ? 'white' : 'transparent'}
                    outline={index === step ? '2px solid' : 'none'}
                    outlineColor={index === step ? 'blue.400' : 'transparent'}
                  >
                    <Icon
                      as={stepItem.icon}
                      boxSize={4.5}
                      color={index <= step ? 'white' : 'gray.400'}
                    />
                  </Flex>
                  <Text
                    fontSize="2xs"
                    fontWeight={index === step ? '800' : '600'}
                    color={index <= step ? 'gray.800' : 'gray.400'}
                    textTransform="uppercase"
                    letterSpacing="0.5px"
                    textAlign="center"
                    noOfLines={1}
                  >
                    {stepItem.label}
                  </Text>
                </VStack>
              ))}
            </Flex>
            <Box bg="gray.100" h="6px" borderRadius="full" overflow="hidden" mt={3}>
              <Box
                h="100%"
                w={`${progress}%`}
                bgGradient="linear(to-r, teal.400, blue.500)"
                borderRadius="full"
                transition="width 0.4s"
              />
            </Box>
          </Box>

          {/* Step Content */}
          {renderStep()}

          {/* Step Navigation */}
          {renderStepNavigation()}
        </VStack>
        )}
      </Container>
    </Box>
  );
}
